import React, { useMemo } from 'react';
import { useSubscriptions } from '@features/subscriptions/context/subscription-context';
import { Divider, FlexView, Tag } from '@goodfynd/ui';
import { addClassNames } from '@goodfynd/theme';
import { Text } from '@goodfynd/ui';
import { renderPrice } from '@utils/number-util';
import { pluralize } from '@utils/string-util';

import type { ActiveSubscriptionsSummaryProps } from './types';

import classes from './ActiveSubscriptionsSummary.module.css';

export const ActiveSubscriptionsSummary = ({
  className,
}: ActiveSubscriptionsSummaryProps) => {
  const {
    activeSubscriptions: subscriptions,
    activeSubscriptionsTotal,
    cartSubtotal,
    hasUpdates,
    subscriptionItems,
  } = useSubscriptions();

  const subscriptionFrequency = useMemo(() => {
    return subscriptions[0]?.frequency;
  }, [subscriptions]);

  const isTrialEligible = useMemo(() => {
    return subscriptions.some((s) => s.isTrialEligible);
  }, [subscriptions]);

  const trialItemSkus = useMemo(() => {
    if (!isTrialEligible) return new Set<string>();
    const skus = new Set<string>();
    subscriptions.forEach((sub) => {
      if (!sub.isTrialEligible) return;
      sub.items.forEach((item) => {
        if (item.price.trialPeriod) {
          skus.add(item.price.sku);
        }
      });
    });
    return skus;
  }, [isTrialEligible, subscriptions]);

  const hasTrialItems = trialItemSkus.size > 0;

  const maxTrialDays = useMemo(() => {
    let max = 0;
    subscriptions.forEach((sub) => {
      sub.items.forEach((item) => {
        if (item.price.trialPeriod && trialItemSkus.has(item.price.sku)) {
          max = Math.max(max, item.price.trialPeriod);
        }
      });
    });
    return max;
  }, [subscriptions, trialItemSkus]);

  const subscriptionsTotalDisplay = useMemo(() => {
    if (subscriptionFrequency === 'annual') {
      return `${renderPrice(activeSubscriptionsTotal)}/year`;
    }

    return `${renderPrice(activeSubscriptionsTotal)}/month`;
  }, [subscriptionFrequency, activeSubscriptionsTotal]);

  const currentTotalDisplay = useMemo(() => {
    if (!hasTrialItems) return subscriptionsTotalDisplay;

    // Subtract trial item prices from total
    let trialItemsTotal = 0;
    subscriptionItems.forEach((item) => {
      if (trialItemSkus.has(item.price.sku)) {
        trialItemsTotal += item.price.price * item.quantity;
      }
    });
    const currentTotal = activeSubscriptionsTotal - trialItemsTotal;
    const suffix = subscriptionFrequency === 'annual' ? '/year' : '/month';
    return `${renderPrice(currentTotal)}${suffix}`;
  }, [
    activeSubscriptionsTotal,
    hasTrialItems,
    subscriptionFrequency,
    subscriptionItems,
    subscriptionsTotalDisplay,
    trialItemSkus,
  ]);

  const futureSubscriptionsTotalDisplay = useMemo(() => {
    const futureTotal = activeSubscriptionsTotal + cartSubtotal;
    if (subscriptionFrequency === 'annual') {
      return `${renderPrice(futureTotal)}/year`;
    }

    return `${renderPrice(futureTotal)}/month`;
  }, [activeSubscriptionsTotal, cartSubtotal, subscriptionFrequency]);

  if (!subscriptionItems[0]) {
    return null;
  }

  return (
    <div className={addClassNames(classes.container, className)}>
      <FlexView direction="column" gap={24} width="100%">
        <Text as="subhead2" truncate={1}>
          Summary
        </Text>

        <FlexView direction="column" gap={16} width="100%">
          {subscriptionItems.map((item) => {
            const isTrial = trialItemSkus.has(item.price.sku);

            return (
              <FlexView
                gap={16}
                justify="space-between"
                key={item.itemId}
                width="100%"
              >
                <FlexView direction="column" gap={4}>
                  <FlexView align="center" gap={8}>
                    <Text>{item.product.name}</Text>
                    {isTrial && (
                      <Tag
                        label={`${item.price.trialPeriod}-day trial`}
                        type="positive"
                      />
                    )}
                  </FlexView>
                  {item.product.allowMultiple && (
                    <Text as="body2">
                      {item.quantity} {pluralize('seat', item.quantity)}
                    </Text>
                  )}
                </FlexView>

                <Text>
                  {isTrial
                    ? '$0.00'
                    : renderPrice(item.price.price * item.quantity)}
                </Text>
              </FlexView>
            );
          })}
        </FlexView>

        <Divider />

        <FlexView gap={16} justify="space-between" width="100%">
          <Text as="subhead1">Total</Text>

          <Text as="subhead1">{currentTotalDisplay}</Text>
        </FlexView>

        {hasTrialItems && (
          <FlexView direction="column" gap={4} width="100%">
            <FlexView gap={16} justify="space-between" width="100%">
              <Text as="subhead1" color="TextIconSecondary">
                After trial
              </Text>
              <Text as="subhead1" color="TextIconSecondary">
                {subscriptionsTotalDisplay}
              </Text>
            </FlexView>
          </FlexView>
        )}

        {hasUpdates && (
          <FlexView gap={16} justify="space-between" width="100%">
            <Text as="subhead1" color="TextIconFavorable">
              Future total
            </Text>

            <Text as="subhead1" color="TextIconFavorable">
              {futureSubscriptionsTotalDisplay}
            </Text>
          </FlexView>
        )}
      </FlexView>
    </div>
  );
};
